/**
 * Fordonsskatt: Car.info g201 (betalningsmånader) → senast sista dagen i månaden.
 * Avisering 45 dagar före den dagen. Håll synkad med backend/src/services/vehicles/taxDueCalc.ts.
 */

export const TAX_REMINDER_DAYS_BEFORE = 45;

const MONTH_ALIASES: Record<string, number> = {
  januari: 1,
  january: 1,
  jan: 1,
  februari: 2,
  february: 2,
  feb: 2,
  mars: 3,
  march: 3,
  mar: 3,
  april: 4,
  apr: 4,
  maj: 5,
  may: 5,
  juni: 6,
  june: 6,
  jun: 6,
  juli: 7,
  july: 7,
  jul: 7,
  augusti: 8,
  august: 8,
  aug: 8,
  september: 9,
  sep: 9,
  sept: 9,
  oktober: 10,
  october: 10,
  okt: 10,
  oct: 10,
  november: 11,
  nov: 11,
  december: 12,
  dec: 12,
};

function isYesValue(raw: unknown): boolean {
  const v = String(Array.isArray(raw) ? raw[0] : raw ?? '')
    .trim()
    .toLowerCase();
  return v === 'ja' || v === 'yes' || v === 'true' || v === 'y' || v === '1';
}

export function extractTaxMonthValues(g201: {
  values?: unknown[] | null;
  attributes?: Record<string, { name?: string; values?: unknown[] }> | null;
} | null | undefined): unknown[] {
  if (g201?.values?.length) return g201.values;
  const attrs = g201?.attributes;
  if (!attrs || typeof attrs !== 'object') return [];
  const names: string[] = [];
  for (const child of Object.values(attrs)) {
    if (!child || !isYesValue(child.values)) continue;
    const name = String(child.name ?? '').trim();
    if (name) names.push(name);
  }
  return names;
}

export function parseTaxPaymentMonths(values: unknown[] | undefined | null): number[] {
  if (!values?.length) return [];
  const months = new Set<number>();
  for (const raw of values) {
    const parts = String(raw ?? '').split(/[,;/|]+/);
    for (const part of parts) {
      const token = part.trim().toLowerCase().replace(/\./g, '');
      if (!token) continue;
      if (/^\d{1,2}$/.test(token)) {
        const n = Number(token);
        if (n >= 1 && n <= 12) months.add(n);
        continue;
      }
      const n = MONTH_ALIASES[token];
      if (n) months.add(n);
    }
  }
  return [...months].sort((a, b) => a - b);
}

export function startOfLocalDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

export function lastDayOfMonth(year: number, month1to12: number): Date {
  return new Date(year, month1to12, 0);
}

export function calendarDaysUntil(target: Date, asOf: Date): number {
  const a = startOfLocalDay(asOf).getTime();
  const b = startOfLocalDay(target).getTime();
  return Math.round((b - a) / 86_400_000);
}

export function formatTaxDueIso(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function nextTaxDueDate(months: number[], asOf: Date): Date | null {
  if (!months.length) return null;
  const today = startOfLocalDay(asOf);
  let best: Date | null = null;
  for (let yearOffset = 0; yearOffset <= 1; yearOffset++) {
    const year = today.getFullYear() + yearOffset;
    for (const month of months) {
      const due = startOfLocalDay(lastDayOfMonth(year, month));
      if (due.getTime() < today.getTime()) continue;
      if (!best || due.getTime() < best.getTime()) best = due;
    }
    if (best) return best;
  }
  return best;
}

export interface TaxDueWindow {
  dueDate: Date;
  daysUntil: number;
}

export function taxDueWithinWindow(
  months: number[],
  asOf: Date,
  daysAhead = TAX_REMINDER_DAYS_BEFORE
): TaxDueWindow | null {
  const dueDate = nextTaxDueDate(months, asOf);
  if (!dueDate) return null;
  const daysUntil = calendarDaysUntil(dueDate, asOf);
  if (daysUntil > daysAhead) return null;
  return { dueDate, daysUntil };
}
